import { useCombobox } from 'downshift';
import Fuse from 'fuse.js';
import { isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useUpdateEffect } from 'react-use';
import { useCallbackRef } from '../../../hooks';
import { useConstant } from '../../../hooks/useConstant';
import { useTabs, type TabProps } from '../../Tabs';
import { ALL_TAB } from '../AutocompleteDropdown/types';
import { createCustomSortFilter } from '../SearchSelect/utils';
import { IGNORED_AUTOCOMPLETE_GROUP } from './tokens';
import type {
  AutocompleteGroup,
  FuseSearchObject,
  FuseSearchResult,
  SortFilterItem,
  UseAutocompleteOutput,
  UseAutocompleteProps,
} from './types';
import { getGroupsMetadata } from './utils';

// Searching options for fuse.js
const fuseOptions = {
  keys: ['label', 'description'],
  includeMatches: true,
  includeScore: true,
} as const;

const DEFAULT_THRESHOLD = 0.5; // default fusejs threshold is 0.6. Keeping it defined here so we can change it.

export function getLabelWrapper<T>(getLabel: ((obj: T) => string | null | undefined) | undefined, obj: T | undefined) {
  if (obj == null) {
    return '';
  } else if (getLabel == null) {
    if (typeof obj === 'string') {
      return obj;
    } else {
      return JSON.stringify(obj);
    }
  } else {
    return getLabel(obj) ?? '';
  }
}

export function getDescriptionWrapper<T>(
  getDescription: ((obj: T) => string | null | undefined) | undefined,
  obj: T | undefined
): string {
  if (getDescription == null || obj == null) {
    return '';
  }
  return getDescription(obj) ?? '';
}

export function useAutocomplete<T>({
  items,
  getLabel,
  getDescription,
  isItemDisabled,
  getGroup,
  initialSortByLabel = true,
  matchThreshold = DEFAULT_THRESHOLD,
  minMatchCharLength,
  searchKeys,
  itemSize,
  additionalSearchKeys,
  fuseDistance,
  sortFilterOverride,
  customFuseSearchResultsSorter,
  disableFuzzyMatching = false,
  simpleSortFilter = false,
  groupSorter,
  inputRef,
  ...props
}: UseAutocompleteProps<T>): UseAutocompleteOutput<T> {
  const isReadyRef = useRef(false);
  const [results, setResults] = useState<FuseSearchResult<T>[]>([]);

  // Created once per component instance — computing locale collation tables on every comparison can be expensive.
  const collator = useMemo(() => new Intl.Collator(), []);

  // This sorting is applied on search results only.
  // When provided, customFuseSearchResultsSorter fully replaces Fuse's default score-based ordering with a custom comparator.
  // Fuse scores are still available on each result and can be used within the comparator.
  // When not provided, Fuse's own ordering (sorted by score) is preserved as-is.
  const maybeSortSearchResults = useCallback(
    (searchResults: FuseSearchResult<T>[], searchString: string) => {
      if (customFuseSearchResultsSorter) {
        return searchResults.toSorted((a, b) => customFuseSearchResultsSorter(a, b, searchString));
      } else {
        return searchResults;
      }
    },
    [customFuseSearchResultsSorter]
  );

  const effectiveSortFilterOverride = useMemo(() => {
    if (sortFilterOverride) {
      return sortFilterOverride;
    }
    if (simpleSortFilter) {
      return createCustomSortFilter<T>(item => `${item.label} ${item.description}`);
    }
    return undefined;
  }, [sortFilterOverride, simpleSortFilter]);

  // We want to store a mapping of group key -> group metadata since we are flattening all groups' items
  // in order to push it through fuse. when the items come back on the other side, we need to stitch
  // the groups back together using this group -> metadata lookup
  // Note in the case of selecting groups the metadata does not change, the items are just filtered
  const groupMetadata = useMemo(() => {
    const itemsAlreadyGrouped = itemsAreAutocompleteGroups(items);
    if (itemsAlreadyGrouped) {
      return new Map(items.map(itemGroup => [itemGroup.group, itemGroup]));
    }
    return getGroupsMetadata(items, getGroup);
  }, [getGroup, items]);

  // After making sure all items are grouped, we sort the groups individually.
  const groups = useMemo(() => {
    const newGroups = [...groupMetadata.values()];
    if (initialSortByLabel) {
      return newGroups.map(group => {
        const keyed = group.items
          .map(item => ({ item, key: getLabelWrapper(getLabel, item) }))
          .toSorted((a, b) => collator.compare(a.key, b.key));
        return { ...group, items: keyed.map(({ item }) => item) };
      });
    }
    if (groupSorter) {
      return newGroups.toSorted(groupSorter);
    }
    return newGroups;
  }, [groupMetadata, initialSortByLabel, getLabel, collator, groupSorter]);

  const initialTabItems = useConstant([ALL_TAB, ...groups.map(group => ({ group: group.group }))]);
  const handleTabSelect = useCallbackRef(() => {
    // Upon clicking a tab item, the input loses focus. Return focus to the input immediately.
    inputRef?.current?.focus();
  });

  // We now have grouped and sorted items. Using this, we can create tabs.
  const tabs = useTabs<TabProps & { group: string }>({
    initialItems: initialTabItems,
    showAddTab: false,
    initialSelectedIndex: 0,
    onSelect: handleTabSelect,
  });

  const { setItems: setTabItems } = tabs;

  // Keep the state of the tab items in check with the AutocompleteGroups we compute directly above.
  // We already set an initial set of tabs, so skip the initial render here by doing useUpdateEffect.
  useUpdateEffect(() => {
    // The tabs only care about the "group" (key) property, so map to that.
    const newTabItems = [ALL_TAB, ...groups.map(group => ({ group: group.group }))];
    setTabItems(prevTabItems => {
      if (!isEqual(prevTabItems, newTabItems)) {
        return newTabItems;
      }
      return prevTabItems;
    });
  }, [groups, setTabItems]);

  // Given the selected tab ("selectedGroup"), we then filter down the groups to just that group.
  const renderedGroups = useMemo(() => {
    const selectedGroup = tabs.items[tabs.selectedIndex]?.group ?? ALL_TAB.group;
    if (selectedGroup === ALL_TAB.group) {
      return groups;
    }

    return groups.filter(group => group.group === selectedGroup);
  }, [tabs.items, tabs.selectedIndex, groups]);

  // After having pushed all the items through fuse, re-attach items to their groups
  const { resultGroups, flattenedResults } = useMemo(() => {
    const resultsByGroup = results.reduce((map, result) => {
      if (map.has(result.item._group)) {
        map.get(result.item._group)!.push(result);
      } else {
        map.set(result.item._group, [result]);
      }

      return map;
    }, new Map<string, FuseSearchResult<T>[]>());
    let resultGroups: AutocompleteGroup<FuseSearchResult<T>>[] = [...resultsByGroup.entries()]
      .map(([groupKey, items]) => {
        const group = groupMetadata.get(groupKey);
        if (!group) {
          return undefined;
        }

        return {
          ...group,
          items,
        };
      })
      .compact();

    if (groupSorter) {
      resultGroups = resultGroups.toSorted(groupSorter);
    }

    // After re-merging all search results into groups, we need to proide downshift with a flat list of all our items.
    // This list should be of type T[]
    // It's important that we group them and then re-flatten because then we can guarantee the correct ordering of items over several groups
    const flattenedResults = resultGroups.reduce(
      (arr, group) => arr.concat(group.items.map(item => item.item.item)),
      [] as T[]
    );
    return { resultGroups, flattenedResults };
  }, [results, groupMetadata, groupSorter]);

  const combobox = useCombobox<T>({
    defaultHighlightedIndex: 0,
    // Prevent downshift from initialising inputValue to itemToString(selectedItem) on mount.
    // Search selects display the selected item via a separate trigger button, not by populating
    // the search input, so a non-empty initial inputValue would fire an expensive sortFilterOverride
    // call with the selection label before the dropdown has ever been opened.
    initialInputValue: '',
    items: flattenedResults,
    itemToString: item => {
      return item ? getLabelWrapper(getLabel, item) : '';
    },
    onInputValueChange: ({ inputValue }) => {
      if (inputValue == null) {
        inputValue = '';
      }
    },
    isItemDisabled: (item, index) => {
      // Two issues:
      // - due to the useUpdateEffect re-render, we need to check if the items are ready before we can check if they are disabled
      // - the 'item' checked could be undefined due to internal downshift behavior when the user is typing and the list is empty
      const listEmpty = flattenedResults.length === 0;
      return !isReadyRef.current || listEmpty ? true : (isItemDisabled?.(item, index) ?? false);
    },
    ...props,
  });

  // Pre-build SortFilterItem[] and a group lookup when sortFilterOverride is active.
  // Memoized so it only rebuilds when items change, not on every keystroke.
  // The Map is keyed by SortFilterItem (object reference) rather than T, so primitive T values don't collide.
  const sortFilterData = useMemo(() => {
    if (effectiveSortFilterOverride == null) {
      return null;
    }
    const groupMap = new Map<SortFilterItem<T>, string>();
    const items = renderedGroups.flatMap(group =>
      group.items.map(item => {
        const label = getLabelWrapper(getLabel, item);
        const description = getDescriptionWrapper(getDescription, item);
        const entry: SortFilterItem<T> = {
          item,
          label,
          lowerLabel: label.toLowerCase(),
          description,
          lowerDescription: description.toLowerCase(),
        };
        groupMap.set(entry, group.group);
        return entry;
      })
    );
    return { items, groupMap };
  }, [effectiveSortFilterOverride, renderedGroups, getLabel, getDescription]);

  const fuseSearchObjects = useMemo(() => {
    if (effectiveSortFilterOverride != null) {
      return [] as FuseSearchObject<T>[];
    }

    return renderedGroups.flatMap(group => {
      return group.items.map(item => {
        const wrappedItem: FuseSearchObject<T> = {
          item: item,
          label: getLabelWrapper(getLabel, item),
          description: getDescriptionWrapper(getDescription, item),
          disabled: isItemDisabled ? isItemDisabled(item) : false,
          _group: group.group,
        };

        // If there are any additional search keys provided, apply them to the FuseSearchObject
        if (additionalSearchKeys) {
          for (const additionalSearchKey of additionalSearchKeys) {
            wrappedItem[additionalSearchKey] = item[additionalSearchKey];
          }
        }

        return wrappedItem;
      });
    });
  }, [effectiveSortFilterOverride, renderedGroups, additionalSearchKeys, getLabel, getDescription, isItemDisabled]);

  const shouldSort = customFuseSearchResultsSorter == null;
  const fuse = useMemo(() => {
    if (effectiveSortFilterOverride != null) {
      return new Fuse<FuseSearchObject<T>>([]);
    }

    const fuseKeys: (string | { name: string; weight?: number })[] = [
      ...fuseOptions.keys,
      ...(additionalSearchKeys || []),
    ];

    return new Fuse(fuseSearchObjects, {
      ...fuseOptions,
      threshold: matchThreshold,
      ignoreLocation: true, // ignore _where_ in the string the match occurred. if its there, its there.
      keys: searchKeys ? searchKeys : fuseKeys,
      distance: fuseDistance,
      useExtendedSearch: disableFuzzyMatching,
      minMatchCharLength,
      shouldSort,
    });
  }, [
    effectiveSortFilterOverride,
    fuseSearchObjects,
    searchKeys,
    minMatchCharLength,
    matchThreshold,
    additionalSearchKeys,
    fuseDistance,
    disableFuzzyMatching,
    shouldSort,
  ]);

  // Here we change the search results based on any inputValue changes and any fuse object changes.
  // If fuse object changes when there is a non-empty inputValue for example, we redo the search on the new fuseObject using
  // our pre-existing inputValue search string
  useEffect(() => {
    const inputValue = combobox.inputValue ?? '';
    // Wraps a FuseSearchObject with the neutral ranking metadata used when Fuse scoring is not applicable.
    const wrapUnranked = (item: FuseSearchObject<T>): FuseSearchResult<T> => ({
      item,
      refIndex: 0,
      matches: undefined,
      score: 0,
    });

    if (effectiveSortFilterOverride && sortFilterData) {
      const filtered = inputValue
        ? effectiveSortFilterOverride(sortFilterData.items, inputValue.toLowerCase())
        : sortFilterData.items;
      setResults(
        filtered.map(sortItem =>
          wrapUnranked({
            item: sortItem.item,
            label: sortItem.label,
            description: sortItem.description,
            disabled: isItemDisabled ? isItemDisabled(sortItem.item) : false,
            _group: sortFilterData.groupMap.get(sortItem) ?? IGNORED_AUTOCOMPLETE_GROUP,
          } as FuseSearchObject<T>)
        )
      );
      isReadyRef.current = true;
      return;
    }

    if (!inputValue) {
      setResults(fuseSearchObjects.map(wrapUnranked));
    } else {
      const searchString = inputValue.toLowerCase();
      // https://www.fusejs.io/examples.html#extended-search
      const extendedSearchString = searchString
        .split(' ')
        .filter(n => n)
        .map(item => `'${item}`) // note the ' applied here, see the extended search url above for docs
        .join(' ');

      const results = fuse.search(disableFuzzyMatching ? extendedSearchString : searchString);
      setResults(maybeSortSearchResults(results, searchString));
    }
    isReadyRef.current = true;
  }, [
    combobox.inputValue,
    isItemDisabled,
    fuse,
    maybeSortSearchResults,
    disableFuzzyMatching,
    fuseSearchObjects,
    customFuseSearchResultsSorter,
    effectiveSortFilterOverride,
    sortFilterData,
  ]);
  return {
    groupMetadata,
    searchResults: results.map(item => item.item.item),
    items: flattenedResults,
    searchResultGroups: resultGroups,
    itemSize,
    isItemDisabled,
    tabs,
    ...combobox,
  };
}

export function itemsAreAutocompleteGroups<T>(items: T[] | AutocompleteGroup<T>[]): items is AutocompleteGroup<T>[] {
  if (items.length === 0) {
    return false; // inconclusive, assume false for this render
  }

  const firstItem = items[0];
  if (typeof firstItem !== 'object' || firstItem == null) {
    // If its not an object then its a string or a number meaning it cant be an autocompletegroup item
    return false;
  }

  return itemIsAutocompleteGroup(firstItem);
}

export function itemIsAutocompleteGroup<T>(item: T | AutocompleteGroup<T>): item is AutocompleteGroup<T> {
  return (
    item &&
    typeof item === 'object' &&
    ('items' satisfies keyof AutocompleteGroup<T>) in item &&
    ('group' satisfies keyof AutocompleteGroup<T>) in item
  );
}

/**
 * Takes an array of items, which can be whatever mix of T and AutocompleteGroup<T>, and flattens them to just T[]
 */
export function flattenAutocompleteGroupItems<T extends object>(items: (T | AutocompleteGroup<T>)[]): T[] {
  return items.flatMap(groupOrItem => {
    if (itemIsAutocompleteGroup(groupOrItem)) {
      return groupOrItem.items;
    } else {
      return groupOrItem;
    }
  });
}
